import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarIcon, ArrowLeftIcon, TagIcon, UserIcon } from 'lucide-react';
import { Button } from '../components/Button';
interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  image: string;
  author: string;
  content: React.ReactNode;
}
const posts: BlogPost[] = [{
  slug: 'how-to-access-home-equity-in-canada',
  title: 'How to Access Your Home Equity in Canada: A Complete Guide',
  excerpt: "Millions of Canadian homeowners are sitting on significant equity but don't know how to access it. This guide walks you through every option available.",
  date: 'February 18, 2026',
  category: 'Home Equity',
  author: 'EquityRich Team',
  image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1400&q=85',
  content: <>
        <p data-id="element-380">
          If you've owned your home for several years, there's a good chance
          you're sitting on a significant amount of equity — the difference
          between what your home is worth and what you still owe on your
          mortgage. For many Canadians, this equity represents their single
          largest financial asset. Yet most homeowners have no idea how to
          access it.
        </p>
        <p data-id="element-381">
          This guide breaks down every option available to Canadian homeowners,
          explains the pros and cons of each, and helps you figure out which
          path makes the most sense for your situation.
        </p>

        <h2 data-id="element-382">What Is Home Equity?</h2>
        <p data-id="element-383">
          Home equity is simply the portion of your home that you truly "own."
          If your home is worth $800,000 and you have a $300,000 mortgage
          balance remaining, your equity is $500,000. As you pay down your
          mortgage and as property values rise, your equity grows.
        </p>

        <h2 data-id="element-384">Option 1: Home Equity Line of Credit (HELOC)</h2>
        <p data-id="element-385">
          A HELOC is a revolving line of credit secured against your home. Think
          of it like a credit card, but with your home as collateral and a much
          lower interest rate. You can borrow up to a certain limit, repay it,
          and borrow again as needed.
        </p>
        <ul data-id="element-386">
          <li data-id="element-387">
            <strong data-id="element-388">Best for:</strong> Ongoing expenses, renovations with
            multiple phases, or emergency funds
          </li>
          <li data-id="element-389">
            <strong data-id="element-390">Typical limit:</strong> Up to 65% of your home's appraised
            value (combined with your mortgage, up to 80%)
          </li>
          <li data-id="element-391">
            <strong data-id="element-392">Rate:</strong> Variable, typically prime + 0.5%
          </li>
        </ul>

        <h2 data-id="element-393">Option 2: Second Mortgage</h2>
        <p data-id="element-394">
          A second mortgage is a lump-sum loan secured against your home, in
          addition to your existing first mortgage. Unlike a HELOC, you receive
          all the funds at once and repay them on a fixed schedule.
        </p>
        <ul data-id="element-395">
          <li data-id="element-396">
            <strong data-id="element-397">Best for:</strong> Large one-time expenses like debt
            consolidation or major renovations
          </li>
          <li data-id="element-398">
            <strong data-id="element-399">Typical limit:</strong> Up to 75–80% of your home's value,
            minus your first mortgage balance
          </li>
          <li data-id="element-400">
            <strong data-id="element-401">Rate:</strong> Higher than a first mortgage, but often lower
            than unsecured debt
          </li>
        </ul>

        <h2 data-id="element-402">Option 3: Refinancing Your Mortgage</h2>
        <p data-id="element-403">
          Refinancing means replacing your existing mortgage with a new, larger
          one and pocketing the difference. This can give you access to equity
          at first-mortgage rates, but it comes with prepayment penalties if you
          break your current mortgage early.
        </p>

        <h2 data-id="element-404">Option 4: Reverse Mortgage</h2>
        <p data-id="element-405">
          Available to homeowners aged 55 and older, a reverse mortgage lets you
          access up to 55% of your home's value as tax-free cash — with no
          monthly payments required. The loan is repaid when you sell the home
          or pass away.
        </p>

        <h2 data-id="element-406">Which Option Is Right for You?</h2>
        <p data-id="element-407">
          The right choice depends on your age, how much equity you have, what
          you need the money for, and your ability to make monthly payments. At
          EquityRich, we specialize in equity-based lending — meaning we don't
          require credit checks or income verification. Our decisions are based
          purely on your property's value and location.
        </p>
        <p data-id="element-408">
          Ready to find out how much you can borrow? Get your free assessment
          today — it takes less than two minutes.
        </p>
      </>
}, {
  slug: 'second-mortgage-vs-heloc',
  title: 'Second Mortgage vs. HELOC: Which Is Right for You?',
  excerpt: "Both products let you tap into your home's equity, but they work very differently. We break down the key differences.",
  date: 'February 10, 2026',
  category: 'Mortgages',
  author: 'EquityRich Team',
  image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1400&q=85',
  content: <>
        <p data-id="element-409">
          When Canadian homeowners want to access their equity, two products
          come up most often: the second mortgage and the Home Equity Line of
          Credit (HELOC). While both are secured against your home, they work
          quite differently — and choosing the wrong one can cost you.
        </p>

        <h2 data-id="element-410">The Core Difference</h2>
        <p data-id="element-411">
          A <strong data-id="element-412">second mortgage</strong> gives you a lump sum of money
          upfront, which you repay over a fixed term with regular payments. A{' '}
          <strong data-id="element-413">HELOC</strong> is a revolving credit line — you draw from it
          as needed, repay it, and draw again.
        </p>

        <h2 data-id="element-414">When a Second Mortgage Makes More Sense</h2>
        <ul data-id="element-415">
          <li data-id="element-416">You need a large, specific amount of money right now</li>
          <li data-id="element-417">You want predictable monthly payments</li>
          <li data-id="element-418">You're consolidating high-interest debt</li>
          <li data-id="element-419">Your credit score doesn't qualify you for a HELOC</li>
        </ul>

        <h2 data-id="element-420">When a HELOC Makes More Sense</h2>
        <ul data-id="element-421">
          <li data-id="element-422">You need ongoing access to funds over time</li>
          <li data-id="element-423">You're doing a phased renovation project</li>
          <li data-id="element-424">You want flexibility to borrow only what you need</li>
          <li data-id="element-425">You have a strong credit profile</li>
        </ul>

        <h2 data-id="element-426">The Bottom Line</h2>
        <p data-id="element-427">
          For most homeowners who need a defined amount of money for a specific
          purpose, a second mortgage offers simplicity and certainty. For those
          who need flexible, ongoing access to funds, a HELOC is typically the
          better fit.
        </p>
        <p data-id="element-428">
          At EquityRich, we offer both products — and we'll help you figure out
          which one suits your situation. Contact us for a free, no-obligation
          assessment.
        </p>
      </>
}, {
  slug: 'debt-consolidation-with-home-equity',
  title: 'Using Home Equity to Consolidate Debt: Is It Worth It?',
  excerpt: 'High-interest credit card debt can be crushing. Many homeowners use their equity to pay it all off at a much lower rate.',
  date: 'January 28, 2026',
  category: 'Debt Consolidation',
  author: 'EquityRich Team',
  image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1400&q=85',
  content: <>
        <p data-id="element-429">
          The average Canadian carries thousands of dollars in high-interest
          debt — credit cards, car loans, personal lines of credit. If you're a
          homeowner, you may have a powerful tool to eliminate that debt
          quickly: your home equity.
        </p>

        <h2 data-id="element-430">How Debt Consolidation with Home Equity Works</h2>
        <p data-id="element-431">
          You take out a second mortgage or HELOC secured against your home, use
          those funds to pay off all your high-interest debts, and then make a
          single, lower monthly payment on the equity loan. Because the loan is
          secured by real estate, the interest rate is dramatically lower than
          credit card rates.
        </p>

        <h2 data-id="element-432">The Numbers Make Sense</h2>
        <p data-id="element-433">
          Consider this: if you have $50,000 in credit card debt at 20%
          interest, you're paying $10,000 per year in interest alone. A second
          mortgage at 8% on the same amount costs just $4,000 per year — a
          saving of $6,000 annually.
        </p>

        <h2 data-id="element-434">The Risk to Understand</h2>
        <p data-id="element-435">
          The key risk is that you're converting unsecured debt into secured
          debt. If you default on a credit card, your credit score takes a hit.
          If you default on a mortgage, you could lose your home. This is why
          debt consolidation works best when paired with a commitment to not
          accumulate new high-interest debt.
        </p>

        <h2 data-id="element-436">Is It Right for You?</h2>
        <p data-id="element-437">
          Debt consolidation with home equity is a powerful strategy — but it
          requires discipline. If you're ready to break the cycle of
          high-interest debt, contact us to find out how much equity you can
          access.
        </p>
      </>
}];
const fallbackPost: BlogPost = {
  slug: '',
  title: 'Article Not Found',
  excerpt: '',
  date: '',
  category: '',
  author: '',
  image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1400&q=85',
  content: <p data-id="element-438">This article could not be found. Please return to the blog.</p>
};
const categoryColors: Record<string, string> = {
  'Home Equity': 'bg-emerald-100 text-emerald-800',
  Mortgages: 'bg-blue-100 text-blue-800',
  'Debt Consolidation': 'bg-orange-100 text-orange-800',
  'Reverse Mortgages': 'bg-purple-100 text-purple-800',
  'Home Renovation': 'bg-yellow-100 text-yellow-800',
  'Market Insights': 'bg-gray-100 text-gray-800'
};
export function BlogPostPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = posts.find(p => p.slug === slug) || fallbackPost;
  return <main className="w-full" data-id="element-439">
      {/* Hero Image */}
      <div className="relative w-full h-[420px] md:h-[520px] overflow-hidden" data-id="element-440">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-441" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-442" />
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-10 max-w-4xl mx-auto w-full" data-id="element-443">
          <div className="max-w-4xl mx-auto" data-id="element-444">
            {post.category && <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-4 ${categoryColors[post.category] || 'bg-gray-100 text-gray-800'}`} data-id="element-445">
                {post.category}
              </span>}
            <h1 className="text-3xl md:text-5xl font-serif text-white font-[500] leading-tight" data-id="element-446">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Article Meta + Content */}
      <section className="w-full bg-cream py-12 px-4" data-id="element-447">
        <div className="max-w-3xl mx-auto" data-id="element-448">
          {/* Back link */}
          <Link to="/blog" className="inline-flex items-center text-sm text-primary hover:text-accent transition-colors mb-8 font-medium" data-id="element-449">
            <ArrowLeftIcon className="h-4 w-4 mr-1.5" aria-hidden="true" data-id="element-450" />
            Back to Blog
          </Link>

          {/* Meta bar */}
          <div className="flex flex-wrap items-center gap-5 text-sm text-gray-500 mb-10 pb-8 border-b border-gray-200" data-id="element-451">
            <span className="flex items-center" data-id="element-452">
              <CalendarIcon className="h-4 w-4 mr-1.5 text-primary" aria-hidden="true" data-id="element-453" />
              {post.date}
            </span>
          </div>

          {/* Article Body */}
          <article className="prose-article" data-id="element-454">{post.content}</article>

          {/* CTA Card */}
          <div className="mt-16 relative overflow-hidden bg-gradient-to-br from-[#07493a] to-[#1a6b55] rounded-2xl p-8 md:p-10 text-center" data-id="element-455">
            <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse 60% 80% at 80% 20%, rgba(201,169,97,0.15) 0%, rgba(201,169,97,0.06) 40%, transparent 70%)'
          }} aria-hidden="true" data-id="element-456" />
            <div className="relative z-10" data-id="element-457">
              <h3 className="text-2xl md:text-3xl font-serif text-white font-[500] mb-3" data-id="element-458">
                Ready to Access Your Home Equity?
              </h3>
              <p className="text-gray-300 mb-6" data-id="element-459">
                No credit check. No income verification. Funds in as little as
                48 hours.
              </p>
              <Button variant="primary" size="lg" icon href="/#form" data-id="element-460">
                Get My Free Assessment
              </Button>
            </div>
          </div>

          {/* Back to blog */}
          <div className="mt-10 text-center" data-id="element-461">
            <Link to="/blog" className="inline-flex items-center text-primary hover:text-accent font-medium transition-colors" data-id="element-462">
              <ArrowLeftIcon className="h-4 w-4 mr-2" aria-hidden="true" data-id="element-463" />
              View All Articles
            </Link>
          </div>
        </div>
      </section>
    </main>;
}